import * as atlas from 'azure-maps-control';
import { Palette } from '@mui/material';
import * as polyline from '@mapbox/polyline';
import * as util from 'util';

import {
  MappableStop,
  Stringable,
  CalculateRouteRequest,
  Coordinates,
  RouteContextInfo,
  ShipmentStatus,
  Location,
  RouteDetail,
  CalculatedRoute
} from '@/common/components/tracking-map/MapTypes';
import { ShipmentStop, VisibilityBooking } from '@/common/apis/dtos';
import { calculateRoute } from '@/common/apis/map-routes-query';
import { getStopIcon, getDestinationIcon } from './MapIcons';

import './MapInformation.scss';
import {
  Disruption,
  Feature,
  ResultOption,
  RouteType,
  RoutingProfile,
  Waypoint
} from '@/common/constants/map-display-types';
import { StopType } from '@/common/constants/booking-types';

export const MAX_COORD_PRECISION = 5;
export const PADDING_MAP_CENTER = 70;
export const COMPLETED_ROUTE_DATASOURCE_ID = 'datasource_completed';
export const NOT_COMPLETED_ROUTE_DATASOURCE_ID = 'datasource_not_completed';
export const STROKE_WIDTH = 3;
export const SYMBOL_LAYER_ID = 'layer_symbol';
export const ROUTING_PROFILE = RoutingProfile.MappingGlobalTruck;
export const V_OFFSET_STOP = 16;
export const V_OFFSET_STOP_POPUP = -2;
export const DATASOURCE_ID = 'datasource_disruptions';
export const POLYGON_LAYER_ID = 'disruptions_polygon';
export const BORDER_LAYER_ID = 'disruptions_border';

type translateFnType = (resourceKey: string, replaceVariable?: Stringable[]) => string;

export const getPopupContent = (waypoint: Waypoint, completed: boolean, translate: translateFnType) => (
   `<div class="popup-info">
    <div class='stop'>
      ${translate(waypoint.stopTypeLabel)} (${translate(completed ? 'Complete' : 'Pending')})
    </div>
    <div class='location'>
      <span class='mask'>${translate(waypoint?.userLabel as string)}</span>
      <div>
        ${waypoint?.place?.city}, ${waypoint?.place?.state || waypoint?.place?.country}
      </div>
    </div>
  </div>`
);

export const getDisruptionsPopupContent = (shape: atlas.Shape) => {
  return `<div class='disruption-popup-info'>
  <div class='disruption'>
    ${shape.getProperties()['name']}
  </div>
  <div class='detail'>
    ${shape.getProperties()['text']}
  </div>
  </div>`;
};

export const createWaypoints = (stops: MappableStop[]) => stops.map((stop) => createWaypoint(stop));

export const createWaypoint = (stop: MappableStop): Waypoint => {
  return {
    geoPosition: { latitude: stop?.location?.latitude || 0, longitude: stop?.location?.longitude || 0 },
    userLabel: stop?.location?.name ?? stop?.location?.address1,
    place: {
      city: stop?.location?.city,
      state: stop?.location?.stateCode,
      county: '',
      country: stop?.location?.countryCode,
      zip: stop?.location?.postalCode
    },
    stopTypeLabel: StopType[stop.type ?? -1],
    status: stop?.status
  };
};

export const addRouteFromPolyline = (mapRef: atlas.Map | null, polylines: string[], routeType: RouteType): [number, number][] => {
  const coordinates: [number, number][] = [];
  polylines.forEach(p => polyline.decode(p).map((coords: number[]) => coordinates.push(([coords[1], coords[0]]))));

  const dataSourceRef =
    routeType === RouteType.Completed ? (mapRef ? mapRef.sources.getById(COMPLETED_ROUTE_DATASOURCE_ID) as atlas.source.DataSource : null) :
      routeType === RouteType.NotCompleted ? (mapRef ? mapRef.sources.getById(NOT_COMPLETED_ROUTE_DATASOURCE_ID) as atlas.source.DataSource : null) :
        undefined;

  if (dataSourceRef) {
    dataSourceRef.clear();
    dataSourceRef.add(new atlas.data.Feature(new atlas.data.LineString(coordinates)));
  }

  return coordinates;
};

export const getOrderRoutes = (stops: MappableStop[], booking: VisibilityBooking) => {
  const orderStatus = booking.status?.description;

  const routeDetail: RouteDetail = {
    stops,
    status: orderStatus as ShipmentStatus,
    showTime: false,
    showStopRedesign: false
  };

  const routePayload = createCalculateRoutePayload(
    routeDetail as RouteContextInfo,
    ROUTING_PROFILE
  );

  return calculateRoute(routePayload);
};

export const validDataSource = (dataSource: atlas.source.DataSource, disruptions: Disruption[]) => dataSource && disruptions.length > 0;

export const isMapLoaded = (isMapReady: boolean, map: atlas.Map | null) => isMapReady && map;

export const getGeometryCoordinates = (feature: Feature) => feature.geometry?.coordinates ? feature.geometry?.coordinates[0] as [number, number][] : [];

export const addDisruptionsToDataSource = (isMapReady: boolean, map: atlas.Map, disruptions: Disruption[]) => {
  const mapIsReady = isMapLoaded(isMapReady, map);
  if (mapIsReady) {
    const dataSource = getDataSource(map) as atlas.source.DataSource;
    const isDataSourceValid = validDataSource(dataSource, disruptions);
    if (isDataSourceValid) {
      disruptions.forEach((disruption) => {
        disruption.affectedArea?.features?.forEach((feature) => {
          const coordinates: [number, number][] = getGeometryCoordinates(feature);

          const shape: [number, number][] = [...coordinates, coordinates[0]];
          dataSource.add(new atlas.data.Feature(new atlas.data.Polygon(shape), {
            name: disruption.name,
            text: feature.properties ? feature.properties['name'] : '',
            updated: feature.properties ? feature.properties['updatedOn'] : ''
          }));

          dataSource.add(new atlas.data.Feature(new atlas.data.LineString(shape)));
        });
      });
    }
  }
};

export const createMapLayers = (
  map: atlas.Map,
  isMapReady: boolean,
  setMapStyle: (mapStyle: string) => void,
  palette: Palette,
  setMapError: React.Dispatch<React.SetStateAction<Error | undefined>>
) => {
  const mapIsReady = isMapLoaded(isMapReady, map);
  if (mapIsReady) {
    map.events.add('stylechanged', (e: atlas.StyleChangedEvent) => setMapStyle(e.style as MapStyles));

    map.events.addOnce('error', (e: atlas.MapErrorEvent) => {
      if (util.inspect(e.error) === '[Error: Failed to fetch]') {
        setMapError(e.error);
      }
    });

    if (map.sources.getById(NOT_COMPLETED_ROUTE_DATASOURCE_ID) === undefined) {
      const notCompletedDataSourceRef = new atlas.source.DataSource(NOT_COMPLETED_ROUTE_DATASOURCE_ID);
      map.sources.add(notCompletedDataSourceRef);
      // add not completed route layer for all possible map styles but only one visible.
      map.layers.add(
        createNotCompletedRouteLayer(
          notCompletedDataSourceRef,
          getRouteId(
            map.getStyle().style as MapStyles,
            NOT_COMPLETED_ROUTE_DATASOURCE_ID
          ),
          map.getStyle().style as MapStyles,
          true,
          palette.map.routes.notCompleted
        ),
        SYMBOL_LAYER_ID
      );
    }

    if (map.sources.getById(COMPLETED_ROUTE_DATASOURCE_ID) === undefined) {
      const dataSourceRef = new atlas.source.DataSource(COMPLETED_ROUTE_DATASOURCE_ID);
      map.sources.add(dataSourceRef);
      map.layers.add(
        createRouteLayer(
          dataSourceRef,
          getRouteId(
            map.getStyle().style as MapStyles,
            COMPLETED_ROUTE_DATASOURCE_ID
          ),
          map.getStyle().style as MapStyles,
          true,
          palette.map.routes.completed
        ),
        SYMBOL_LAYER_ID
      );
      map.layers.add(createSymbolLayer(dataSourceRef));
    }
  }
};
export const addRoutesToMap = (
  map: atlas.Map | null,
  isMapReady: boolean,
  routes: CalculatedRoute[] | undefined,
  waypoints: Waypoint[],
  isCameraFocusedOnRoutes: boolean,
  isError: boolean,
  isLoading: boolean,
  setIsCameraFocusedOnRoutes: (isCameraFocusedOnRoutes: boolean) => void,
  setShowErrorToast: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const mapIsReady = isMapLoaded(isMapReady, map);
  if (mapIsReady && !isLoading) {
    setShowErrorToast(isError);
    let coordinates: atlas.data.Position[] = [];
    if (routes) {
      routes.forEach((calculatedRoute) => {
        if (calculatedRoute.route?.polylines !== undefined && calculatedRoute.route?.polylines.length > 0) {
          const routeCoords = addRouteFromPolyline(map, calculatedRoute.route?.polylines, calculatedRoute.routeType);
          coordinates = coordinates.concat(routeCoords);
        }
      });
    }

    // adjust the map camera based on the route coordinates; If not available, just use the waypoints.
    const routePositions: atlas.data.Position[] =
      coordinates.length === 0
        ? waypoints.map(p => [p?.geoPosition?.longitude, p?.geoPosition?.latitude]) as atlas.data.Position[]
        : coordinates;

    if (!isCameraFocusedOnRoutes && map) {
      setIsCameraFocusedOnRoutes(!isCameraFocusedOnRoutes);
      map.setCamera({
        bounds: createBoundingBox(routePositions),
        padding: PADDING_MAP_CENTER
      });
    }
  }
};

export const getDisruptionsLayers = (
  map: atlas.Map,
  isMapReady: boolean,
  palette: Palette,
  popup: atlas.Popup
) => {
  const mapIsReady = isMapLoaded(isMapReady, map);
  if (mapIsReady) {
    const dataSource = getDataSource(map);
    const newLayers = {
      areas: new atlas.layer.PolygonLayer(dataSource, POLYGON_LAYER_ID, {
        fillColor: palette.map.disruptions.secondary,
        fillOpacity: palette.map.disruptions.secondaryOpacity
      }),
      borders: new atlas.layer.LineLayer(dataSource, BORDER_LAYER_ID, {
        strokeColor: palette.map.disruptions.primary,
        strokeWidth: 1,
        strokeOpacity: 0.25
      })
    };

    map.events.add('click', newLayers.areas, (e) => {
      const targetElement = (e ? e.originalEvent?.target as HTMLElement : null);
      if (targetElement?.tagName.toLowerCase() !== 'canvas') {
        return;
      }

      popup.close();
      const polygon = e ? e.shapes?.find(shape => (shape as atlas.Shape).getType() === 'Polygon') : null;
      popup.setOptions({
        content: getDisruptionsPopupContent(polygon as atlas.Shape),
        position: e.position
      });

      popup.open(map);
    });

    map.layers.add([newLayers.areas, newLayers.borders]);

    return newLayers;
  }
};

export const getDataSource = (map: atlas.Map): atlas.source.Source<atlas.source.SourceEvents> => {
  if (map.sources.getById(DATASOURCE_ID) === undefined) {
    map.sources.add(new atlas.source.DataSource(DATASOURCE_ID));
  }

  return map.sources.getById(DATASOURCE_ID);
};

export const convertToMappableStops = (shipmentStops: ShipmentStop[]) => {
  const stops: MappableStop[] = [];

  shipmentStops.forEach(shipmentStop => {
    const location = shipmentStop.location ?? {};

    const stop: MappableStop = {
      location: {
        name: location.name,
        address1: location.physicalAddress?.addressLine1,
        address2: location.physicalAddress?.addressLine2,
        city: location.physicalAddress?.city,
        countryCode: location.physicalAddress?.country,
        stateCode: location.physicalAddress?.stateProvince,
        postalCode: location.physicalAddress?.postalCode,
        latitude: location.physicalAddress?.latitude,
        longitude: location.physicalAddress?.longitude
      } as Location,
      billToIds: [] as string[],
      itemDetails: {
        orders: [1, 2, 3, 4],
        pallets: 0,
        quantity: 0,
        value: 0,
        weight: {
          metric: 0,
          standard: 0
        },
        items: []
      },
      stopId: shipmentStop.stopId ?? '',
      stopNumber: shipmentStop.stopNumber ?? 0,
      type: shipmentStop.type ?? StopType['Pickup From'],
      status: shipmentStop.activities?.[0]?.departureDateTime ? ShipmentStatus.Delivered : ShipmentStatus.None,
      stopDates: []
    };

    stops.push(stop);
  });

  return stops;
};

export const createCalculateRoutePayload = (
  { stops }: RouteContextInfo,
  routingProfile: RoutingProfile) => {
  const completedStops = stops.filter((stop) => stop.status === ShipmentStatus.Delivered);
  const notCompletedStops = stops.filter((stop) => stop.status !== ShipmentStatus.Delivered);
  const completedWaypoints = completedStops.map((stop) => createWaypoint(stop));
  const notCompletedWaypoints = notCompletedStops.map((stop) => createWaypoint(stop));
  const routePayload: CalculateRouteRequest[] = [];

  if (completedWaypoints.length > 0) {
    routePayload.push({
      waypoints: completedWaypoints,
      includeShape: false,
      resultOptions: [ResultOption.Polyline],
      routeType: RouteType.Completed,
      routingProfile
    });
  }

  if (notCompletedWaypoints.length > 0) {
    routePayload.push({
      waypoints: notCompletedWaypoints,
      includeShape: false,
      resultOptions: [ResultOption.Polyline],
      routeType: RouteType.NotCompleted,
      routingProfile
    });
  }

  if (completedWaypoints.length > 0 && notCompletedWaypoints.length > 0) {
    // in the case where we only have a start and end and no stops in between
    // remove the completed waypoint from the route payload in favor of not completed route
    if (completedWaypoints.length === 1) {
      routePayload.shift();
    }

    // if we are missing last known location coordinates, display the entire last route as not completed
    // so we still display the route and not an error when trying to build the route.
    notCompletedWaypoints.unshift(completedWaypoints[completedWaypoints.length - 1]);
  }

  return routePayload;
};

// Filter invalid waypoints based on coordinates.
export const filterInvalidWaypoints = (waypoints: Waypoint[]) => {
  return waypoints.filter((waypoint) => {
    return isValidCoordinates(waypoint.geoPosition as Coordinates);
  });
};

export const isValidCoordinates = (coordinates: Coordinates): boolean => {
  return coordinates !== undefined &&
    coordinates?.latitude !== undefined &&
    coordinates?.longitude !== undefined &&
    !(coordinates.latitude === 0 && coordinates.longitude === 0) &&
    !(coordinates.latitude > 90 || coordinates.latitude < -90) &&
    !(coordinates.longitude > 180 || coordinates.longitude < -180);
};

export enum MapStyles {
  road = 'road',
  grayscale_dark = 'grayscale_dark',
  satellite = 'satellite'
}

export const createRouteLayer = (map: atlas.source.DataSource | string, layerId: string, mapStyle: MapStyles, isVisible: boolean, strokeColor: string): atlas.layer.LineLayer => {
  return new atlas.layer.LineLayer(map, layerId, { strokeColor, strokeWidth: STROKE_WIDTH, visible: isVisible });
};

export const createNotCompletedRouteLayer = (map: atlas.source.DataSource | string, layerId: string, mapStyle: MapStyles, isVisible: boolean, strokeColor: string): atlas.layer.LineLayer => {
  return new atlas.layer.LineLayer(map, layerId, {
    strokeColor,
    strokeWidth: STROKE_WIDTH,
    strokeDashArray: [1, 3],
    visible: isVisible
  });
};

export const createSymbolLayer = (map: atlas.source.DataSource): atlas.layer.SymbolLayer => {
  return new atlas.layer.SymbolLayer(map, SYMBOL_LAYER_ID, {
    iconOptions: {
      image: ['get', 'icon'],
      allowOverlap: true
    },
    filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']]
  });
};

export const getIcon = (type: StopType, status: boolean) => {
  switch (type) {
    case StopType['Deliver To']:
      return getDestinationIcon(status);
    case StopType['Pickup From']:
    default:
      return getStopIcon(status);
  }
};

// Create a Marker for each waypoint based on the `type` argument.
export const createMarkers =
  (waypoints: Waypoint[], type: StopType, translate: translateFnType) => {
    return waypoints.map(waypoint => {
      const coordinates = waypoint.geoPosition;
      const position = new atlas.data.Position(coordinates?.longitude as number, coordinates?.latitude as number);

      return new atlas.HtmlMarker({
        htmlContent: getIcon(type, waypoint.status === ShipmentStatus.Delivered),
        position: position,
        text: translate(waypoint.userLabel as string),
        pixelOffset: [0, V_OFFSET_STOP],
        popup:
          new atlas.Popup({
            content: getPopupContent(waypoint, waypoint.status === ShipmentStatus.Delivered, translate),
            pixelOffset: [0, V_OFFSET_STOP_POPUP],
            position: position
          })
      });
    });
  };

export const filterLayers = (id: string) => {
  return id.includes(COMPLETED_ROUTE_DATASOURCE_ID) || id.includes(NOT_COMPLETED_ROUTE_DATASOURCE_ID);
};

export const handleRouteUpdates = (map: atlas.Map, newMapStyle: string, palette: Palette) => {
  if (map?.layers) {
    // remove the existing route line layers.
    const routeLineLayers = map.layers.getLayers().filter(l => filterLayers(l.getId()));
    map.layers.remove(routeLineLayers);
  }

  if (map) {
    const completedSource = map.sources ? map.sources.getById(COMPLETED_ROUTE_DATASOURCE_ID) : null;
    if (completedSource != null) {
      map.layers.add(
        createRouteLayer(
          completedSource.getId(),
          getRouteId(
            newMapStyle as MapStyles,
            COMPLETED_ROUTE_DATASOURCE_ID
          ),
          newMapStyle as MapStyles,
          true,
          palette.map.routes.completed
        ));
    }
  }

  const notCompletedSource = map && map.sources ? map.sources.getById(NOT_COMPLETED_ROUTE_DATASOURCE_ID) : null;
  if (notCompletedSource != null) {
    map?.layers.add(
      createNotCompletedRouteLayer(
        notCompletedSource.getId(),
        getRouteId(
          newMapStyle as MapStyles,
          NOT_COMPLETED_ROUTE_DATASOURCE_ID
        ),
        newMapStyle as MapStyles,
        true,
        palette.map.routes.notCompleted
      ));
  }
};

export const handleMarkerUpdates = (map: atlas.Map) => {
  if (!map.markers) {
    return;
  }

  map.markers.getMarkers().forEach(marker => {
    const innerHtml = marker.getElement().innerHTML;
    const hasKnownStopType =
      innerHtml.includes('Pickup From') || innerHtml.includes('Deliver To') || innerHtml.includes('None');

    if (hasKnownStopType) {
      marker.setOptions({ htmlContent: getStopIcon(false) });
    }
  });
};

export const getMapMarkers = (waypoints: Waypoint[], translate: translateFnType) => {
  const [pickup, destination, ...intermediaryWaypoints] = [
    waypoints[0],
    waypoints[waypoints.length - 1],
    ...waypoints.slice(1, -1)
  ];

  const markers: atlas.HtmlMarker[] = [];

  if (pickup) {
    markers.push(...createMarkers([pickup], StopType['Pickup From'], translate));
  }

  if (intermediaryWaypoints.length > 0) {
    markers.push(...createMarkers(intermediaryWaypoints, StopType['None'], translate));
  }

  if (destination) {
    markers.push(...createMarkers([destination], StopType['Deliver To'], translate));
  }

  return markers;
};

export const getRouteId = (mapStyle: string, dataSourceId: string): string => {
  return `${mapStyle}-${dataSourceId}`;
};

export const areNumbersEqual = (value1: number, value2: number): boolean => {
  const precision = MAX_COORD_PRECISION - 1;
  return value1.toFixed(precision) === value2.toFixed(precision);
};

// There are cases where the completed points are adjusted for trans-pacific routes while
// the non-completed are not. Or visa-versa. This will ensure that all are adjusted.
export const adjustTransPacificRoutes = (positions: atlas.data.Position[]): atlas.data.Position[] => {
  let factor = 0;
  const adjusted: atlas.data.Position[] = [positions[0]];
  positions.forEach((start, i) => {
    if (i < positions.length - 1) {
      const destination = positions[i + 1];
      const longitudeDifference = destination[0] - start[0];
      const areLatitudesEqual = areNumbersEqual(start[1], destination[1]);
      if (areLatitudesEqual && areNumbersEqual(longitudeDifference, 360)) {
        factor -= 1;
      } else if (areLatitudesEqual && areNumbersEqual(longitudeDifference, -360)) {
        factor += 1;
      }

      const coord = [parseFloat((destination[0] + (factor * 360)).toFixed(MAX_COORD_PRECISION)), destination[1]];
      adjusted.push(coord);
    }
  });

  return adjusted;
};

// The Bounding Box is an array of the 4 outer directional limits of the points and looks like this
// [left.longitude, bottom.latitude, right.longitude, top.latitude]
export const createBoundingBox = (positions: atlas.data.Position[]): number[] => {
  // There are cases where the completed points are adjusted for trans-pacific routes while
  // the non-completed are not. Or visa-versa. This will ensure that all are adjusted.

  const adjusted = adjustTransPacificRoutes(positions);
  const longitudes = adjusted.map(c => c[0]);
  const left = Math.min(...longitudes);
  const right = Math.max(...longitudes);

  const latitudes = adjusted.map(c => c[1]);
  const bottom = Math.min(...latitudes);
  const top = Math.max(...latitudes);

  return [left, bottom, right, top];
};

export const dedupWaypoints = (waypoints: Waypoint[], plottedPoints: Set<string>) => {
  return waypoints.filter((waypoint) => {
    const key = waypoint?.geoPosition?.latitude + ',' + waypoint?.geoPosition?.longitude;
    const dup = plottedPoints.has(key);
    plottedPoints.add(key);
    return !dup;
  });
};
